import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { storage, db } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import SimpleActivityUploader from '../components/SimpleActivityUploader';
import UploadTest from '../components/UploadTest';
import BackButton from '../components/BackButton';
import { createYourMissingActivity } from '../utils/createMissingActivity';
import { FaCheckCircle, FaArrowRight, FaPlus, FaExclamationTriangle, FaSpinner, FaWrench } from 'react-icons/fa';

const ActivityUploadPage = () => {
  const { user } = useAuth();
  const [uploadedActivities, setUploadedActivities] = useState([]);
  const [creatingActivity, setCreatingActivity] = useState(false);

  const handleUploadComplete = (downloadURL, activityNumber, activityId) => {
    setUploadedActivities(prev => [...prev, { activityNumber, downloadURL, activityId }]);
  };

  const createMissingActivity = async (activityNumber) => {
    if (!user) return;

    try {
      setCreatingActivity(true);
      
      // Check if activity image exists in storage
      let imageUrl = null;
      const formats = ['png', 'jpg', 'jpeg', 'webp'];
      
      for (const format of formats) {
        try {
          const imageRef = ref(storage, `pdf-pages/${user.uid}/page_${activityNumber}.${format}`);
          imageUrl = await getDownloadURL(imageRef);
          break;
        } catch (error) {
          continue;
        }
      }

      if (!imageUrl) {
        alert(`No image found for Activity ${activityNumber}. Please upload the image first.`);
        return;
      }

      // Create activity record in Firestore
      const activityData = {
        number: activityNumber,
        title: `Daily Activity ${activityNumber}`,
        description: `Creative activity for developing artistic thinking - Activity ${activityNumber}`,
        content: [
          `Welcome to Activity ${activityNumber}!`,
          `This is a creative activity designed to help you develop your artistic skills and imagination.`,
          `Follow the instructions in the image and create something amazing!`
        ],
        imageUrl: imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: 'daily_activity',
        difficulty: 'Beginner',
        duration: '20 minutes',
        materials: 'Paper, pencils, crayons, markers, or any art supplies you prefer',
        category: 'Creative Expression'
      };

      const activityRef = await addDoc(collection(db, 'activities'), activityData);
      
      alert(`✅ Activity ${activityNumber} created successfully!\nFirestore ID: ${activityRef.id}`);
      
      setUploadedActivities(prev => [...prev, { 
        activityNumber, 
        downloadURL: imageUrl, 
        activityId: activityRef.id,
        created: true 
      }]);

    } catch (error) {
      console.error('Error creating activity:', error);
      alert('❌ Error creating activity: ' + error.message);
    } finally {
      setCreatingActivity(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex justify-center px-4 py-8 md:py-10">
        <div className="relative z-10 w-full max-w-3xl">
          <div className="bg-white/95 rounded-[32px] shadow-2xl border-2 border-amber-300 p-8 text-center">
            <h1 className="text-2xl font-bold text-amber-800 mb-4">Please Log In</h1>
            <p className="text-amber-600">You need to be logged in to upload activity images.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex justify-center px-4 py-8 md:py-10">
      <div className="relative z-10 w-full max-w-4xl">
        <div className="bg-white/95 rounded-[32px] shadow-2xl border-2 border-amber-300 p-6 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <BackButton to="/artist-wall" />
          <h1 className="text-3xl font-bold text-amber-800 text-center mt-4">
            🎨 Activity Image Uploader
          </h1>
          <p className="text-amber-600 text-center mt-2">
            Upload your activity images to make them available in the frontend
          </p>
        </div>

        {/* User Info */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 text-center">
          <p className="text-amber-700">
            <strong>Logged in as:</strong> {user.email}
          </p>
          <p className="text-amber-600 text-sm">
            User ID: {user.uid}
          </p>
        </div>

        {/* Upload Section */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Activity 2 Uploader */}
          <div>
            <SimpleActivityUploader 
              activityNumber={2} 
              onUploadComplete={handleUploadComplete}
            />
          </div>

          {/* Activity 3 Uploader */}
          <div>
            <SimpleActivityUploader 
              activityNumber={3} 
              onUploadComplete={handleUploadComplete}
            />
          </div>
        </div>

        {/* Success Messages */}
        {uploadedActivities.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-500" />
              Recently Uploaded Activities
            </h2>
            <div className="space-y-3">
              {uploadedActivities.map((activity, index) => (
                <div key={index} className="flex items-center justify-between bg-green-50 rounded-lg p-3 border border-green-200">
                  <div>
                    <p className="font-semibold text-green-700">
                      Activity {activity.activityNumber} uploaded successfully!
                    </p>
                    <p className="text-green-600 text-sm">
                      URL: {activity.downloadURL.substring(0, 50)}...
                    </p>
                  </div>
                  <FaArrowRight className="text-green-500" />
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-700 text-sm">
                <strong>Next Steps:</strong> Go to your Daily Art Activity page to see the uploaded activities!
              </p>
            </div>
          </div>
        )}

        {/* Fix Missing Activities */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
            <FaExclamationTriangle className="text-yellow-500" />
            Fix Missing Activities
          </h2>
          <p className="text-amber-700 mb-4">
            If you're getting 404 errors or "completed all activities" message, you may need to create missing activity records.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={() => createMissingActivity(1)}
              disabled={creatingActivity}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500 disabled:from-yellow-300 disabled:to-yellow-300 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
            >
              {creatingActivity ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaPlus />
                  Create Activity 1
                </>
              )}
            </button>
            <button
              onClick={() => createMissingActivity(2)}
              disabled={creatingActivity}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500 disabled:from-yellow-300 disabled:to-yellow-300 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
            >
              {creatingActivity ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaPlus />
                  Create Activity 2
                </>
              )}
            </button>
            <button
              onClick={() => createMissingActivity(3)}
              disabled={creatingActivity}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500 disabled:from-yellow-300 disabled:to-yellow-300 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
            >
              {creatingActivity ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaPlus />
                  Create Activity 3
                </>
              )}
            </button>
          </div>
          <p className="text-amber-600 text-sm mt-3">
            <strong>Note:</strong> This will create activity records in Firestore for any uploaded images. Make sure you have uploaded the activity images first.
          </p>
          
          {/* Special button for your missing activity */}
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">🔧 Fix Missing Activity</h3>
            <p className="text-yellow-700 text-sm mb-3">
              If you uploaded an activity image directly to Firebase Storage but it's not showing up, click this button to create the missing Firestore document.
            </p>
            <button
              onClick={async () => {
                try {
                  setCreatingActivity(true);
                  await createYourMissingActivity();
                  alert('✅ Missing activity document created! Now refresh the page and try the Daily Art Activity button again.');
                } catch (error) {
                  alert('❌ Error creating missing activity: ' + error.message);
                } finally {
                  setCreatingActivity(false);
                }
              }}
              disabled={creatingActivity}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-600 hover:to-yellow-500 disabled:from-yellow-300 disabled:to-yellow-300 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
            >
              {creatingActivity ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaWrench />
                  Create Missing Activity Document
                </>
              )}
            </button>
          </div>
        </div>

        {/* Upload Test */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-amber-800 mb-4">🔧 Test Upload Permissions</h2>
          <p className="text-amber-700 mb-4">
            If you're getting permission errors, use this test to verify your Firebase Storage access.
          </p>
          <UploadTest />
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-amber-800 mb-4">📋 How It Works</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-amber-700 mb-2">Upload Process:</h3>
              <ul className="text-amber-600 text-sm space-y-1">
                <li>• Select an image file (PNG, JPG, JPEG, WEBP)</li>
                <li>• File is uploaded to Firebase Storage</li>
                <li>• Activity record created in Firestore</li>
                <li>• Automatically appears in your frontend</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-amber-700 mb-2">Frontend Integration:</h3>
              <ul className="text-amber-600 text-sm space-y-1">
                <li>• Activities appear in Daily Art Activity page</li>
                <li>• Users can complete activities and get feedback</li>
                <li>• All activities are saved to Activity Dashboard</li>
                <li>• Progress is tracked automatically</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default ActivityUploadPage;
